import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { usePageHeaderStore, ActionButton } from '../stores/pageHeader';
import {
  LayoutDashboard,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Moon,
  Sun,
  Edit3,
  Check,
  Chrome
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { TreeSidebar } from '../components/TreeSidebar';
import { Logo } from '../components/Logo';
import clsx from 'clsx';

// Editable Title Component
function EditableTitle({
  title,
  isEditable,
  onTitleChange,
}: {
  title: string;
  isEditable: boolean;
  onTitleChange?: (newTitle: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (editValue.trim() && editValue !== title) {
      onTitleChange?.(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(title);
      setIsEditing(false);
    }
  };

  if (!isEditable) {
    return (
      <h1 className="text-xl font-display font-bold text-neutral-900 dark:text-neutral-100 truncate">
        {title}
      </h1>
    );
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="text-xl font-display font-bold text-neutral-900 dark:text-neutral-100 bg-transparent border-b-2 border-accent-500 outline-none px-0 py-0.5 min-w-[200px]"
        />
        <button
          onClick={handleSave}
          className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 text-accent-600 dark:text-accent-400"
        >
          <Check className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="group flex items-center gap-2 text-left"
    >
      <h1 className="text-xl font-display font-bold text-neutral-900 dark:text-neutral-100 truncate group-hover:text-accent-700 dark:group-hover:text-accent-400 transition-colors">
        {title}
      </h1>
      <Edit3 className="w-4 h-4 text-neutral-400 dark:text-neutral-500 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

// Action Button Component
function ActionButtonItem({ action }: { action: ActionButton }) {
  const baseClasses = 'inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-md transition-colors';
  
  const variantClasses = {
    primary: 'bg-accent-600 text-white hover:bg-accent-700',
    secondary: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-600 border border-neutral-200 dark:border-neutral-600',
    ghost: 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-neutral-100',
    danger: 'bg-error-50 dark:bg-error-900/30 text-error-600 dark:text-error-400 hover:bg-error-100 dark:hover:bg-error-900/50',
  };

  const className = clsx(
    baseClasses,
    variantClasses[action.variant || 'secondary'],
    action.disabled && 'opacity-50 cursor-not-allowed'
  );

  if (action.href) {
    return (
      <Link to={action.href} className={className} title={action.label}>
        {action.icon}
        <span className="hidden sm:inline">{action.label}</span>
      </Link>
    );
  }

  return (
    <button
      onClick={action.onClick}
      disabled={action.disabled}
      className={className}
      title={action.label}
    >
      {action.icon}
      <span className="hidden sm:inline">{action.label}</span>
    </button>
  );
}

export function DashboardLayout() {
  const { user, logout } = useAuthStore();
  const { title, subtitle, icon, badge, isEditable, onTitleChange, actions } = usePageHeaderStore();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);
  
  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);
  
  const toggleTheme = () => {
    setIsDark(!isDark);
    if (isDark) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-neutral-0 dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700 flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Close button for mobile */}
        <div className="lg:hidden absolute top-3 right-3">
          <button
            className="p-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tree Navigation */}
        <TreeSidebar />
      </aside>
      
      {/* Main content */}
      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar - unified header with logo, title and dashboard */}
        <header className="sticky top-0 z-30 h-14 bg-neutral-0/95 dark:bg-neutral-800/95 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-700">
          <div className="h-full flex items-center justify-between px-4 lg:px-6">
            {/* Left side - mobile menu + logo + dashboard */}
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {/* Logo and brand name */}
              <Link to="/app" className="flex items-center">
                <Logo height={28} />
              </Link>
              
              {/* Separator */}
              <div className="hidden sm:block w-px h-6 bg-neutral-200 dark:bg-neutral-700" />
              
              {/* Dashboard link */}
              <nav className="hidden sm:flex items-center gap-1">
                <NavLink
                  to="/app"
                  end
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                      isActive
                        ? 'bg-accent-50 dark:bg-accent-900/30 text-accent-700 dark:text-accent-400'
                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-neutral-100'
                    )
                  }
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </NavLink>
              </nav>
            </div>
            
            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Chrome Extension link */}
              <a
                href="https://chromewebstore.google.com/detail/specuno/aekdfaafpbgmibdejhkpmpkhpbipbnjf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-md transition-colors bg-accent-50 dark:bg-accent-900/30 text-accent-700 dark:text-accent-400 hover:bg-accent-100 dark:hover:bg-accent-900/50"
                title="Get Chrome Extension"
              >
                <Chrome className="w-4 h-4" />
                <span className="hidden sm:inline">Extension</span>
              </a>
              
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              
              {/* User profile */}
              <div className="relative" ref={userMenuRef}>
                <button
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="avatar avatar-sm">
                    {user?.fullName?.[0] || user?.email[0].toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate max-w-[120px]">
                      {user?.fullName || user?.email}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-neutral-500 dark:text-neutral-400 transition-transform ${
                    userMenuOpen ? 'rotate-180' : ''
                  }`} />
                </button>
                
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-neutral-0 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg dark:shadow-neutral-900/50 py-1 z-50">
                    <div className="px-3 py-2 border-b border-neutral-200 dark:border-neutral-700">
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                        {user?.fullName || user?.email}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {user?.isSuperAdmin ? 'Super Admin' : 'User'}
                      </p>
                    </div>
                    {/* Mobile-only Dashboard link */}
                    <div className="sm:hidden border-b border-neutral-200 dark:border-neutral-700">
                      <NavLink
                        to="/app"
                        end
                        className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </NavLink>
                    </div>
                    {/* Settings link for all users */}
                    <NavLink
                      to="/app/settings"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </NavLink>
                    <button
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        
        {/* Page Title Section */}
        {title && (
          <div className="bg-neutral-0 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 px-4 lg:px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {/* Title area */}
              <div className="flex items-center gap-3 min-w-0">
                {icon && (
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--primary-dim)' }}
                  >
                    {icon}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <EditableTitle
                      title={title}
                      isEditable={isEditable || false}
                      onTitleChange={onTitleChange}
                    />
                    {badge}
                  </div>
                  {subtitle && (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate mt-0.5">{subtitle}</p>
                  )}
                </div>
              </div>
              
              {/* Action buttons */}
              {actions && actions.length > 0 && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  {actions.map((action) => (
                    <ActionButtonItem key={action.id} action={action} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
